// Regras puras do menu Documentação (manual do usuário por perfil).
// Formato do HTML e regras: ERPApi/docs/funcionalidades/manual-do-usuario.md (RN-MAN-01..04).

export interface GuideInfo {
  profile: string;
  title: string;
  permission: string;
}

/** Manuais existentes, na ordem das abas do admin. Perfil novo: entrada aqui e no ERPApi (internal/userguide). */
export const GUIDES: GuideInfo[] = [
  { profile: 'seller', title: 'Vendedor', permission: 'user_guide.seller' },
  { profile: 'logistics_operator', title: 'Operador Logístico', permission: 'user_guide.logistics_operator' },
];

export const GUIDE_PERMISSIONS = GUIDES.map((g) => g.permission);

export interface GuideTopic {
  id: string;
  title: string;
  /** Texto puro do tópico (título + corpo), usado na busca. */
  text: string;
}

export interface GuideTab {
  id: string;
  title: string;
  html: string;
  topics: GuideTopic[];
}

export interface ParsedGuide {
  title: string;
  updated: string;
  tabs: GuideTab[];
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

/** Decodifica as entidades HTML mais comuns e as numéricas. */
export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) => {
    if (e[0] === '#') {
      const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : m;
    }
    return ENTITIES[e.toLowerCase()] ?? m;
  });
}

/** Texto puro de um trecho HTML (tags viram espaço). */
export function htmlToText(html: string): string {
  return decodeEntities(html.replace(/<[^>]+>/g, ' ')).replace(/\s+/g, ' ').trim();
}

/** Minúsculas e sem acento, para a busca. */
export function normalizeText(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

const reArticle = /<article class="user-guide"[^>]*data-title="([^"]*)"[^>]*data-updated="([^"]*)"/;
const reSection = /<section data-tab="([^"]+)" data-title="([^"]+)">([\s\S]*?)<\/section>/g;
const reTopicStart = /<div class="topic" id="([^"]+)">\s*<h3>([\s\S]*?)<\/h3>/g;

/**
 * Lê o fragmento HTML do manual (formato validado no ERPApi): cada <section> é uma aba e cada
 * <div class="topic" id> é um tópico de busca. O corpo do tópico vai até o início do próximo.
 */
export function parseGuide(html: string): ParsedGuide {
  const art = reArticle.exec(html);
  const tabs: GuideTab[] = [];
  for (const sec of html.matchAll(reSection)) {
    const [, id, title, body] = sec;
    const starts = [...body.matchAll(reTopicStart)];
    const topics = starts.map((m, i) => {
      const end = i + 1 < starts.length ? starts[i + 1].index! : body.length;
      return { id: m[1], title: htmlToText(m[2]), text: htmlToText(body.slice(m.index!, end)) };
    });
    tabs.push({ id, title: decodeEntities(title), html: body, topics });
  }
  return { title: art ? decodeEntities(art[1]) : '', updated: art ? art[2] : '', tabs };
}

/** Palavras da busca, normalizadas, sem repetição; ignora palavras de 1 letra. */
export function searchTerms(query: string): string[] {
  return [...new Set(normalizeText(query).split(/[^\p{L}\p{N}]+/u).filter((t) => t.length >= 2))];
}

export interface SearchHit {
  tabId: string;
  tabTitle: string;
  topicId: string;
  title: string;
  snippet: string;
  titleMatch: boolean;
}

/** Trecho do texto em volta da primeira palavra encontrada (~ 180 caracteres). */
export function snippetFor(text: string, terms: string[], radius = 90): string {
  const norm = normalizeText(text); // NFD + remoção de acento preserva o comprimento em pt-BR comum
  let pos = -1;
  for (const t of terms) {
    const i = norm.indexOf(t);
    if (i >= 0 && (pos < 0 || i < pos)) pos = i;
  }
  if (pos < 0 || norm.length !== text.length) return text.slice(0, radius * 2) + (text.length > radius * 2 ? '…' : '');
  const start = Math.max(0, pos - radius);
  const end = Math.min(text.length, pos + radius);
  return (start > 0 ? '…' : '') + text.slice(start, end).trim() + (end < text.length ? '…' : '');
}

/**
 * Busca por palavra-chave (RN-MAN-03): o tópico entra se contém TODAS as palavras (título ou
 * texto), sem diferenciar maiúsculas nem acentos. Tópicos cujo título casa vêm primeiro; depois,
 * a ordem do manual.
 */
export function searchTopics(guide: ParsedGuide, query: string): SearchHit[] {
  const terms = searchTerms(query);
  if (terms.length === 0) return [];
  const hits: (SearchHit & { order: number })[] = [];
  let order = 0;
  for (const tab of guide.tabs) {
    for (const topic of tab.topics) {
      order++;
      const text = normalizeText(topic.text);
      if (!terms.every((t) => text.includes(t))) continue;
      const title = normalizeText(topic.title);
      hits.push({
        tabId: tab.id,
        tabTitle: tab.title,
        topicId: topic.id,
        title: topic.title,
        snippet: snippetFor(topic.text, terms),
        titleMatch: terms.some((t) => title.includes(t)),
        order,
      });
    }
  }
  hits.sort((a, b) => Number(b.titleMatch) - Number(a.titleMatch) || a.order - b.order);
  return hits.map(({ order: _order, ...h }) => h);
}

/** Divide o texto em partes marcando as palavras da busca (para <mark>), sem passar HTML. */
export function highlightParts(text: string, terms: string[]): { text: string; match: boolean }[] {
  const norm = normalizeText(text);
  if (terms.length === 0 || norm.length !== text.length) return [{ text, match: false }];
  const marks = new Array<boolean>(text.length).fill(false);
  for (const t of terms) {
    let i = norm.indexOf(t);
    while (i >= 0) {
      for (let k = i; k < i + t.length; k++) marks[k] = true;
      i = norm.indexOf(t, i + t.length);
    }
  }
  const parts: { text: string; match: boolean }[] = [];
  for (let i = 0; i < text.length; i++) {
    const last = parts[parts.length - 1];
    if (last && last.match === marks[i]) last.text += text[i];
    else parts.push({ text: text[i], match: marks[i] });
  }
  return parts;
}

/** Aba que contém o tópico (para links internos e resultados da busca). */
export function tabOfTopic(guide: ParsedGuide, topicId: string): string | undefined {
  return guide.tabs.find((t) => t.topics.some((tp) => tp.id === topicId))?.id;
}
